/**
 * Code-based TanStack Router. The route tree is split into:
 *
 *   __root__
 *     ├── _anon          — pages that require *no* session (/login)
 *     │     └── /login
 *     └── _auth          — pages that require a session; redirects 401 → /login
 *           ├── /        (redirects to /home)
 *           ├── /home
 *           ├── /settings
 *           ├── /settings/change-password
 *           └── /poc/gantt
 *
 * Note: prior `/field/*` (lead read-only workspace) was removed
 * in 17 §1.7; the new `/field/auth/*` + worker timesheet UI lives
 * under a separate WorkerShell layout (see 17 §3.x).
 *
 * The auth check is driven by `meQueryOptions` so both the router guard
 * and the React `useMe` hook share the same cache entry — no duplicate
 * /me requests on initial load.
 */
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
  RouterProvider,
  useRouterState,
} from "@tanstack/react-router";
import { lazy, Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { QueryClient } from "@tanstack/react-query";
import type { RouterHistory } from "@tanstack/react-router";
import { TooltipProvider } from "@/components/ui/tooltip";
import { RouteFallback } from "@/layout/RouteFallback";
import { ChunkErrorBoundary } from "@/layout/ChunkErrorBoundary";
import { HomePage } from "@/pages/HomePage";
import { FieldTimesheetPage } from "@/pages/FieldTimesheetPage";
import { FieldSignPage } from "@/pages/FieldSignPage";
import { LoginPage } from "@/pages/LoginPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { ChangePasswordPage } from "@/pages/ChangePasswordPage";
import { ProjectsPage } from "@/pages/ProjectsPage";
import type { ProjectStatus } from "@/api/projects";
import type { ProjectTab } from "@/pages/ProjectDetailPage";
import { ApprovalsPage } from "@/pages/ApprovalsPage";
import { NewProjectPage } from "@/pages/NewProjectPage";
import { PeopleDetailPage } from "@/pages/PeopleDetailPage";
import { NewPersonPage } from "@/pages/NewPersonPage";
import { PEOPLE_TABS, isPeopleTab, type PeopleTab } from "@/lib/peopleHub";
import type { PeopleFilters } from "@/api/people";
import type { PeopleAvailabilitySearch } from "@/pages/PeopleHubPage";
import type { CatalogTab } from "@/pages/AdminWorkTypesPage";
import { RisksPage } from "@/pages/RisksPage";
import {
  TimeApprovalPage,
  TIME_APPROVAL_TABS,
  type TimeApprovalTab,
} from "@/pages/TimeApprovalPage";
import { meQueryOptions, useMe } from "@/hooks/useMe";
import { peopleCapabilitiesQueryOptions } from "@/hooks/usePeopleCapabilities";
import { isMobileDevice } from "@/lib/device";
import {
  actsOnUnit,
  isProjectManager,
  tracksTime,
  useActsOnUnit,
  useSession,
} from "@/store/session";
import { FEATURES } from "@/config/featureFlags";
import { LeadHubPage } from "@/pages/LeadHubPage";
import { LeadApprovalPage } from "@/pages/LeadApprovalPage";
import { FieldWorkStatusPage } from "@/pages/FieldWorkStatusPage";
import { addDays, format, parseISO } from "date-fns";

interface RouterContext {
  queryClient: QueryClient;
}

// ── Code-split heavy pages (task 106 §1.2) ─────────────────────────────
// Vendor-heavy routes (SVAR Gantt, xyflow + dagre, recharts, admin, templates)
// load on demand so /login, /field and the other first-paint routes don't ship
// them in the entry bundle. Direct-component routes use `lazyRouteComponent`
// at the route definition below. The prop-injecting wrapper routes
// (ProjectDetail / UnitDetail / TemplateEditor / ApprovalDetail read
// search/params and cannot compose with `lazyRouteComponent`) keep an eager
// wrapper and defer the heavy page through `React.lazy` + a local <Suspense>.
const ProjectDetailPage = lazy(() =>
  import("@/pages/ProjectDetailPage").then((m) => ({ default: m.ProjectDetailPage })),
);
const WorkStatusPage = lazy(() =>
  import("@/pages/WorkStatusPage").then((m) => ({ default: m.WorkStatusPage })),
);
const UnitDetailPage = lazy(() =>
  import("@/pages/UnitDetailPage").then((m) => ({ default: m.UnitDetailPage })),
);
const PeopleHubPage = lazy(() =>
  import("@/pages/PeopleHubPage").then((m) => ({ default: m.PeopleHubPage })),
);
const TemplateEditorPage = lazy(() =>
  import("@/pages/TemplateEditorPage").then((m) => ({ default: m.TemplateEditorPage })),
);
const ApprovalDetailPage = lazy(() =>
  import("@/pages/ApprovalDetailPage").then((m) => ({ default: m.ApprovalDetailPage })),
);
const CatalogsPage = lazy(() =>
  import("@/pages/CatalogsPage").then((m) => ({ default: m.CatalogsPage })),
);

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <TooltipProvider delayDuration={300}>
      <Outlet />
    </TooltipProvider>
  ),
  notFoundComponent: RootNotFound,
});

function RootNotFound(): React.ReactElement {
  const { t } = useTranslation();
  return <p>{t("common.notFound")}</p>;
}

// Post-login / index landing target by role + device (task 71 §6.1, 79 §2.8, 73 §2.1, 96 §2.1, 97 §2.1, 98 §2.2, 99 §2.3).
//   - worker                        → /field (PWA)
//   - действует на юнит + mobile    → /field/lead (мобильный хаб §6.2)
//   - действует на юнит + desktop   → /time-approval (подтверждение времени, 79 §2.8)
//   - office_staff + desktop        → /time-approval (единый вход учёта времени, self-вид, 99 §2.3)
//   - pmo + desktop                 → /projects («Учёт времени» открывается пунктом рейла, 99 §2.3)
//   - прочие офисные + mobile       → /field (мобилка = «только ввод времени», 73 §2.1)
//   - admin/viewer + desktop        → /home (office-приложение)
// «Действует на юнит» — руководитель юнита ИЛИ активный заместитель из орг-дерева
// и рёбер делегации (task 98), а не роль: любой, кто ведёт/замещает юнит,
// приземляется на подтверждение времени. Так чистый зам (office_staff без своих
// ведомых) тоже попадает в консоль. Ветка pmo→/projects стоит СТРОГО после
// actsOnUnit-ветки: seed-pmo — лид юнита ПМО, поэтому pmo-лид уходит на консоль,
// а plain-pmo — на портфель. Десктоп-приложение под мобайл не адаптировано,
// поэтому с телефона любая роль уходит в ввод времени.
//
// `tracksTimeFlag` (task 110 §2.1) — участвует ли человек в учёте времени.
// worker всегда приземляется на /field: там же рендерится мягкое состояние
// «учёт отключён», если флаг снят (решение владельца — не хард-редирект).
// Офисному БЕЗ флага self-поверхности не даём: с десктопа он уходит на /home
// (офис-приложение), с мобилы — на /field (мягкое состояние).
function landingTarget(
  role: string | undefined,
  actsOnUnitFlag: boolean,
  tracksTimeFlag: boolean,
): "/field" | "/field/lead" | "/time-approval" | "/projects" | "/home" {
  const mobile = isMobileDevice();
  if (role === "worker") return "/field";
  if (actsOnUnitFlag) return mobile ? "/field/lead" : "/time-approval";
  if (role === "office_staff") {
    if (tracksTimeFlag) return mobile ? "/field" : "/time-approval";
    // Toggled-off office_staff без консоли: self-ввода нет. С мобилы — мягкое
    // состояние «трекинг отключён» на /field (решение владельца — вести некуда).
    // С десктопа у него есть реальная поверхность /projects (в отличие от worker,
    // который только на /field), поэтому уводим туда, а не на упразднённый /home
    // (задача 100 §5.1) — симметрично plain pmo ниже.
    return mobile ? "/field" : "/projects";
  }
  // Все не-лиды (admin / pmo / viewer / прочие) стартуют на /projects — дашборд
  // /home пока не несёт информации и никому не нужен как лендинг (задача 100 §5.1,
  // владелец 11.07). Лид любой роли ушёл веткой actsOnUnit выше → /time-approval
  // (решение задачи 99 цело). /home остаётся в коде, но недостижим: landingTarget
  // его не возвращает → гейт /home всех уводит на /projects.
  return mobile ? "/field" : "/projects";
}

// ── /login (anonymous) ─────────────────────────────────────────────────
const anonRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_anon",
  component: () => <Outlet />,
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.fetchQuery(meQueryOptions);
    if (me) {
      throw redirect({ to: landingTarget(me.role, actsOnUnit(me), tracksTime(me)) });
    }
  },
});

const loginRoute = createRoute({
  getParentRoute: () => anonRoute,
  path: "/login",
  component: LoginRouteComponent,
});

function LoginRouteComponent(): React.ReactElement {
  const navigate = loginRoute.useNavigate();
  return <LoginPage onSuccess={() => void navigate({ to: "/" })} />;
}

// ── authenticated layout ───────────────────────────────────────────────
function AuthLayout(): React.ReactElement {
  // Calling useMe here mirrors the cached /me into the Zustand store so
  // sidebar / user menu / role gates have a populated session as soon as
  // the route renders.
  useMe();
  return <Outlet />;
}

const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  component: AuthLayout,
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient.fetchQuery(meQueryOptions);
    if (!me) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
    if (me.must_change_password && location.pathname !== "/settings/change-password") {
      throw redirect({ to: "/settings/change-password" });
    }
  },
});

const indexRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/",
  beforeLoad: async ({ context }) => {
    // Landing by role + device (task 71 §6.1, 73 §2.1, 97 §2.1): worker → /field,
    // лид → лид-хаб/подтверждение, офисный с телефона → ввод времени,
    // офисный на десктопе → /home. /me уже в кэше от _auth-гарда.
    const me = await context.queryClient.fetchQuery(meQueryOptions);
    throw redirect({ to: landingTarget(me?.role, actsOnUnit(me), tracksTime(me)) });
  },
});

const homeRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/home",
  beforeLoad: async ({ context }) => {
    // Кто не «живёт» на /home, того возвращаем на его landing: worker → /field,
    // лид → лид-хаб (mobile) / подтверждение (desktop), офисный с телефона
    // → ввод времени (73 §2.1). Десктопный офисный остаётся на /home. Зеркало
    // landingTarget — единый источник правды по редиректу. Task 71 §6.1, 79 §2.8, 97 §2.1.
    const me = await context.queryClient.fetchQuery(meQueryOptions);
    const target = landingTarget(me?.role, actsOnUnit(me), tracksTime(me));
    if (target !== "/home") {
      throw redirect({ to: target });
    }
  },
  component: HomePage,
});

const settingsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/settings",
  component: SettingsPage,
});

const changePasswordRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/settings/change-password",
  component: ChangePasswordPage,
});

const ganttPocRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/poc/gantt",
  component: lazyRouteComponent(() => import("@/poc/gantt"), "GanttPoc"),
});

const taskNodePocRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/poc/task-node",
  component: lazyRouteComponent(() => import("@/poc/task-node-poc"), "TaskNodePoc"),
});

const PROJECT_STATUSES: ProjectStatus[] = [
  "planned",
  "in_progress",
  "on_hold",
  "completed",
  "cancelled",
];

export const projectsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/projects",
  validateSearch: (
    raw: Record<string, unknown>,
  ): { status?: ProjectStatus | "at_risk"; archived?: true } => {
    const out: { status?: ProjectStatus | "at_risk"; archived?: true } = {};
    const status = raw.status;
    if (typeof status === "string") {
      if ((PROJECT_STATUSES as string[]).includes(status)) {
        out.status = status as ProjectStatus;
      } else if (status === "at_risk") {
        out.status = status;
      }
    }
    if (raw.archived === true || raw.archived === "true") {
      out.archived = true;
    }
    return out;
  },
  component: ProjectsPage,
});

const newProjectRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/projects/new",
  component: NewProjectPage,
});

const PROJECT_TABS: ProjectTab[] = ["params", "tasks", "gantt", "risks", "history"];

const projectDetailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/projects/$projectId",
  validateSearch: (raw: Record<string, unknown>): { tab?: ProjectTab } => {
    const tab = raw.tab;
    if (typeof tab === "string" && (PROJECT_TABS as string[]).includes(tab)) {
      return { tab: tab as ProjectTab };
    }
    return {};
  },
  component: ProjectDetailRouteComponent,
});

function ProjectDetailRouteComponent(): React.ReactElement {
  const { projectId } = projectDetailRoute.useParams();
  const search = projectDetailRoute.useSearch();
  const navigate = projectDetailRoute.useNavigate();
  const parsed = Number.parseInt(projectId, 10);
  if (!Number.isFinite(parsed)) {
    return <div className="p-6 text-destructive">Invalid project id</div>;
  }
  const tab: ProjectTab = search.tab ?? "params";
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <ProjectDetailPage
          projectId={parsed}
          tab={tab}
          onTabChange={(next) => {
            void navigate({
              search: { tab: next },
              replace: true,
            });
          }}
        />
      </Suspense>
    </ChunkErrorBoundary>
  );
}

const portfolioRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/portfolio",
  component: lazyRouteComponent(() => import("@/pages/PortfolioPage"), "PortfolioPage"),
});

export const loadRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/load",
  validateSearch: (raw: Record<string, unknown>): { unit?: number } => {
    const unit = Number(raw.unit);
    if (Number.isInteger(unit) && unit > 0) {
      return { unit };
    }
    return {};
  },
  component: lazyRouteComponent(() => import("@/pages/LoadPage"), "LoadPage"),
});

const optionalPositiveId = z.preprocess(
  (value) => (value === "" || value == null ? undefined : Number(value)),
  z.number().int().positive().optional().catch(undefined),
);
const canonicalEventTypeIds = (value: unknown): string | undefined => {
  if (value === "none") return "none";
  if (typeof value !== "string") return undefined;
  const ids = value
    .split(",")
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0);
  if (ids.length === 0) return undefined;
  return [...new Set(ids)].sort((a, b) => a - b).join(",");
};
const canonicalPositiveIds = (value: unknown): number[] | undefined => {
  const raw = Array.isArray(value) ? value : value == null ? [] : [value];
  const ids = raw.map(Number).filter((id) => Number.isInteger(id) && id > 0);
  return ids.length === 0 ? undefined : [...new Set(ids)].sort((a, b) => a - b);
};
const PEOPLE_SEARCH_SCHEMA = z
  .object({
    tab: z.enum(PEOPLE_TABS).catch("employees").default("employees"),
    q: z.preprocess((value) => {
      const normalized =
        typeof value === "string" || typeof value === "number" ? String(value).trim() : "";
      return normalized || undefined;
    }, z.string().max(200).optional()),
    unit_id: optionalPositiveId,
    unit_ids: z.preprocess(canonicalPositiveIds, z.array(z.number().int().positive()).optional()),
    position_ids: z.preprocess(
      canonicalPositiveIds,
      z.array(z.number().int().positive()).optional(),
    ),
    status: z.enum(["active", "inactive", "all"]).optional().catch(undefined),
    include_subtree: z.preprocess(
      (value) => (value === true || value === "true" || value === "1" ? true : undefined),
      z.boolean().optional(),
    ),
    horizon: z.preprocess(
      (value) => Number(value),
      z
        .union([z.literal(1), z.literal(3), z.literal(6), z.literal(12)])
        .optional()
        .catch(undefined),
    ),
    anchor: z.preprocess(
      (value) =>
        typeof value === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(value) ? value : undefined,
      z.string().optional(),
    ),
    types: z.preprocess(canonicalEventTypeIds, z.string().optional()),
  })
  .catchall(z.unknown());

export const peopleRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/people",
  validateSearch: (raw) => PEOPLE_SEARCH_SCHEMA.parse(raw),
  beforeLoad: async ({ context }) => {
    const capabilities = await context.queryClient.fetchQuery(peopleCapabilitiesQueryOptions);
    if (!capabilities.can_read) throw redirect({ to: "/home" });
  },
  component: PeopleHubRouteComponent,
});

function PeopleHubRouteComponent(): React.ReactElement {
  const search = peopleRoute.useSearch();
  const navigate = peopleRoute.useNavigate();
  const searchStr = useRouterState({ select: (state) => state.location.searchStr });
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const availability: PeopleAvailabilitySearch = {
    unitId: search.unit_id,
    includeSubtree: search.unit_id == null ? false : (search.include_subtree ?? false),
    horizon: search.horizon ?? 3,
    anchor: search.anchor ?? format(new Date(), "yyyy-MM"),
    eventTypeIds:
      search.types === undefined
        ? undefined
        : search.types === "none"
          ? []
          : search.types.split(",").map(Number),
  };

  useEffect(() => {
    if (pathname !== "/people") return;
    const rawTab = new URLSearchParams(searchStr).get("tab");
    if (!isPeopleTab(rawTab)) {
      void navigate({
        search: (previous) => ({ ...previous, tab: "employees" }),
        replace: true,
      });
    }
  }, [navigate, pathname, searchStr]);

  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <PeopleHubPage
          tab={search.tab}
          filters={{
            q: search.q,
            unit_ids: search.unit_ids,
            position_ids: search.position_ids,
            status: search.status ?? "active",
          }}
          onTabChange={(tab: PeopleTab) => {
            void navigate({ search: (previous) => ({ ...previous, tab }) });
          }}
          onFiltersChange={(filters: PeopleFilters) => {
            void navigate({
              search: (previous) => ({
                ...previous,
                q: filters.q,
                unit_ids: filters.unit_ids,
                position_ids: filters.position_ids,
                status: filters.status,
              }),
            });
          }}
          availability={availability}
          onAvailabilityChange={(patch, replace = false) => {
            void navigate({
              search: (previous) => ({
                ...previous,
                ...(Object.hasOwn(patch, "unitId") ? { unit_id: patch.unitId } : {}),
                ...(Object.hasOwn(patch, "includeSubtree")
                  ? { include_subtree: patch.includeSubtree || undefined }
                  : {}),
                ...(Object.hasOwn(patch, "horizon") ? { horizon: patch.horizon } : {}),
                ...(Object.hasOwn(patch, "anchor") ? { anchor: patch.anchor } : {}),
                ...(Object.hasOwn(patch, "eventTypeIds")
                  ? {
                      types:
                        patch.eventTypeIds === undefined
                          ? undefined
                          : patch.eventTypeIds.length === 0
                            ? "none"
                            : [...patch.eventTypeIds].sort((a, b) => a - b).join(","),
                    }
                  : {}),
              }),
              replace,
            });
          }}
        />
      </Suspense>
    </ChunkErrorBoundary>
  );
}

const unitDetailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/units/$unitId",
  validateSearch: z.object({
    tab: z.enum(["employees", "availability"]).optional().catch(undefined),
    include_subtree: z.preprocess(
      (value) => (value === true || value === "true" || value === "1" ? true : undefined),
      z.boolean().optional(),
    ),
    q: z.preprocess((value) => {
      const normalized = typeof value === "string" ? value.trim() : "";
      return normalized || undefined;
    }, z.string().max(200).optional()),
    position_ids: z.preprocess(
      canonicalPositiveIds,
      z.array(z.number().int().positive()).optional(),
    ),
    status: z.enum(["active", "inactive", "all"]).optional().catch(undefined),
    horizon: z.preprocess(
      (value) => Number(value),
      z
        .union([z.literal(1), z.literal(3), z.literal(6), z.literal(12)])
        .optional()
        .catch(undefined),
    ),
    anchor: z.preprocess(
      (value) =>
        typeof value === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(value) ? value : undefined,
      z.string().optional(),
    ),
    types: z.preprocess(canonicalEventTypeIds, z.string().optional()),
  }),
  component: UnitDetailRouteComponent,
});

function UnitDetailRouteComponent(): React.ReactElement {
  const { unitId } = unitDetailRoute.useParams();
  const search = unitDetailRoute.useSearch();
  const navigate = unitDetailRoute.useNavigate();
  const id = Number(unitId);
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <UnitDetailPage
          unitId={id}
          tab={search.tab ?? "employees"}
          filters={{
            q: search.q,
            unit_ids: [id],
            include_subtree: search.include_subtree,
            position_ids: search.position_ids,
            status: search.status ?? "active",
          }}
          availability={{
            includeSubtree: search.include_subtree ?? false,
            horizon: search.horizon ?? 3,
            anchor: search.anchor ?? format(new Date(), "yyyy-MM"),
            eventTypeIds:
              search.types === undefined
                ? undefined
                : search.types === "none"
                  ? []
                  : search.types.split(",").map(Number),
          }}
          onTabChange={(tab) => void navigate({ search: (previous) => ({ ...previous, tab }) })}
          onAvailabilityChange={(patch, replace = false) =>
            void navigate({
              search: (previous) => ({
                ...previous,
                ...(Object.hasOwn(patch, "includeSubtree")
                  ? { include_subtree: patch.includeSubtree || undefined }
                  : {}),
                ...(Object.hasOwn(patch, "horizon") ? { horizon: patch.horizon } : {}),
                ...(Object.hasOwn(patch, "anchor") ? { anchor: patch.anchor } : {}),
                ...(Object.hasOwn(patch, "eventTypeIds")
                  ? {
                      types:
                        patch.eventTypeIds === undefined
                          ? undefined
                          : patch.eventTypeIds.length === 0
                            ? "none"
                            : [...patch.eventTypeIds].sort((a, b) => a - b).join(","),
                    }
                  : {}),
              }),
              replace,
            })
          }
          onFiltersChange={(filters) =>
            void navigate({
              search: (previous) => ({
                ...previous,
                q: filters.q,
                position_ids: filters.position_ids,
                status: filters.status,
              }),
            })
          }
        />
      </Suspense>
    </ChunkErrorBoundary>
  );
}

const peopleDetailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/people/$id",
  validateSearch: z.object({ edit: z.literal("profile").optional() }),
  component: PeopleDetailRouteComponent,
});

function PeopleDetailRouteComponent(): React.ReactElement {
  const { id } = peopleDetailRoute.useParams();
  const search = peopleDetailRoute.useSearch();
  const navigate = peopleDetailRoute.useNavigate();
  return (
    <PeopleDetailPage
      userId={Number(id)}
      edit={search.edit === "profile"}
      onEditChange={(editing) =>
        void navigate({ search: editing ? { edit: "profile" } : {}, replace: !editing })
      }
    />
  );
}

const newPersonRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/people/new",
  validateSearch: z.object({ unit_id: optionalPositiveId }),
  component: NewPersonRouteComponent,
});

function NewPersonRouteComponent(): React.ReactElement {
  const search = newPersonRoute.useSearch();
  return <NewPersonPage unitId={search.unit_id} />;
}

const risksRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/risks",
  // Функция «Риски» скрыта общим фича-флагом (задача 100 §1.2): пока флаг off,
  // прямой заход на /risks уводим на /home. Восстановление = флип FEATURES.risks.
  beforeLoad: () => {
    if (!FEATURES.risks) {
      throw redirect({ to: "/home" });
    }
  },
  component: RisksPage,
});

// Аудит больше не несёт второй таб (трекинг времени → «Учёт времени», 82 §1.4),
// поэтому без search-params: одна лента изменений плана.
const auditRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/audit",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.fetchQuery(meQueryOptions);
    if (me?.role !== "admin") {
      throw redirect({ to: "/projects" });
    }
  },
  component: lazyRouteComponent(() => import("@/pages/AuditPage"), "AuditPage"),
});

const calendarRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/calendar",
  beforeLoad: () => {
    throw redirect({ to: "/admin/catalogs", search: { tab: "calendar" } });
  },
});

const adminCatalogsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/admin/catalogs",
  validateSearch: (raw: Record<string, unknown>): { tab: CatalogTab } => {
    const tabs: readonly CatalogTab[] = [
      "positions",
      "workTypes",
      "equipmentTypes",
      "assemblyElements",
      "personEventTypes",
      "calendar",
    ];
    const tab = raw.tab;
    return {
      tab:
        typeof tab === "string" && (tabs as readonly string[]).includes(tab)
          ? (tab as CatalogTab)
          : "positions",
    };
  },
  beforeLoad: async ({ context, search }) => {
    const me = await context.queryClient.fetchQuery(meQueryOptions);
    if (me?.role === "admin") return;
    if (!isProjectManager(me)) {
      throw redirect({ to: "/home" });
    }
    if (search.tab !== "calendar") {
      throw redirect({ to: "/admin/catalogs", search: { tab: "calendar" } });
    }
  },
  component: AdminCatalogsRouteComponent,
});

function AdminCatalogsRouteComponent(): React.ReactElement {
  const search = adminCatalogsRoute.useSearch();
  const navigate = adminCatalogsRoute.useNavigate();
  const { user } = useMe();
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <CatalogsPage
          isAdmin={user?.role === "admin"}
          tab={search.tab}
          onTabChange={(tab) => {
            void navigate({ search: { tab } });
          }}
        />
      </Suspense>
    </ChunkErrorBoundary>
  );
}

// Старый URL `/admin/timesheet-backfill` слит в единый раздел «Учёт времени»
// (`/time-approval`, задача 82 §1.1). Маршрут оставлен живым **только как
// redirect** — внешние ссылки и deep-link «открыть в редакторе» из аудита
// (`?user_id=&date=`) продолжают работать: переносим параметры в канонический
// маршрут (`week_start` выводим из даты, чтобы карточка дня открылась сразу).
const adminBackfillRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/admin/timesheet-backfill",
  validateSearch: (raw: Record<string, unknown>): { user_id?: number; date?: string } => {
    const out: { user_id?: number; date?: string } = {};
    const rawUid = raw.user_id;
    if (typeof rawUid === "number" && Number.isFinite(rawUid) && rawUid > 0) {
      out.user_id = rawUid;
    } else if (typeof rawUid === "string" && /^\d+$/.test(rawUid)) {
      out.user_id = Number(rawUid);
    }
    const rawDate = raw.date;
    if (typeof rawDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
      out.date = rawDate;
    }
    return out;
  },
  beforeLoad: ({ search }) => {
    const next: { user_id?: number; date?: string; week_start?: string } = {};
    if (search.user_id !== undefined) next.user_id = search.user_id;
    if (search.date !== undefined) {
      next.date = search.date;
      next.week_start = isoMonday(parseISO(search.date));
    }
    throw redirect({ to: "/time-approval", search: next });
  },
});

const templatesRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/templates",
  component: lazyRouteComponent(() => import("@/pages/TemplatesPage"), "TemplatesPage"),
});

type TemplateEditorView = "workflow" | "gantt";
const TEMPLATE_EDITOR_VIEWS: TemplateEditorView[] = ["workflow", "gantt"];

const templateEditorRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/templates/$templatePk/edit",
  validateSearch: (raw: Record<string, unknown>): { view?: TemplateEditorView } => {
    const view = raw.view;
    if (typeof view === "string" && (TEMPLATE_EDITOR_VIEWS as string[]).includes(view)) {
      return { view: view as TemplateEditorView };
    }
    return {};
  },
  component: TemplateEditorRouteComponent,
});

function TemplateEditorRouteComponent(): React.ReactElement {
  const { templatePk } = templateEditorRoute.useParams();
  const search = templateEditorRoute.useSearch();
  const navigate = templateEditorRoute.useNavigate();
  const parsed = Number.parseInt(templatePk, 10);
  if (!Number.isFinite(parsed)) {
    return <div className="p-6 text-destructive">Invalid template pk</div>;
  }
  const view: TemplateEditorView = search.view ?? "workflow";
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <TemplateEditorPage
          templatePk={parsed}
          view={view}
          onViewChange={(next) => {
            void navigate({
              search: { view: next },
              replace: true,
            });
          }}
        />
      </Suspense>
    </ChunkErrorBoundary>
  );
}

// ── /time-approval — единственный вход учёта времени (задача 99 §2.1) ────
// Единый вход открыт офисным ролям: admin/pmo/office_staff впускаются по роли,
// лид/зам — по «действует на юнит» (task 97/98). Что внутри — консоль vs
// self-редактор — решает страница (§2.2). worker/viewer → /home.
// `week_start` / `date` — в search-params; дефолты (ISO-понедельник / сегодня)
// проставляет RouteComponent, чтобы ссылка была шарящейся и переживала F5.
function isoMonday(d: Date): string {
  const dow = (d.getDay() + 6) % 7; // 0 = Monday
  return format(addDays(d, -dow), "yyyy-MM-dd");
}

// ── /work-status — реестр статусов работ (задача 114 §2.1) ─────────────
// Экран руководительский: гейт = admin (по роли) ∪ «действует на юнит».
// **`me.acting_unit_ids` у admin ПУСТ** (контракт /api/me — «фронт гейтит
// админа по роли»), поэтому ветка `role === "admin"` обязана стоять отдельно:
// без неё admin не попал бы на собственный экран. Тот же класс ошибок ловился
// живым смоуком в задачах 98 и 99.
const workStatusRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/work-status",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.fetchQuery(meQueryOptions);
    if (me?.role !== "admin" && !actsOnUnit(me)) {
      throw redirect({ to: "/projects" });
    }
    // Device-guard (задача 122 §1.2): до сюда дошёл ровно admin ∪ «действует на
    // юнит» — это в точности аудитория выделенной field-поверхности, поэтому с
    // телефона редиректим туда безусловно. Мобильный офис-апп (рейл + широкие
    // таблицы) под телефон не адаптирован; карточная раскладка статусов работ
    // живёт на /field/lead/work-status. Петли нет: приёмный роут гейтит тем же
    // предикатом, а его reject-ветка ведёт на /field, не назад сюда. Search-
    // параметры при редиректе отбрасываются намеренно — на field-поверхности
    // выбор работы локальный state, URL-адресации там нет.
    if (isMobileDevice()) {
      throw redirect({ to: "/field/lead/work-status" });
    }
  },
  // Состояние экрана — в URL: подразделение админа и выбранная работа. Ссылка
  // на работу переживает перезагрузку и шарится, «назад» возвращает к прошлому
  // выбору. Поиск и фильтр статусов остаются локальными: это способ добраться
  // до строки, а не адрес того, на что смотрим.
  validateSearch: (
    raw: Record<string, unknown>,
  ): { unit_id?: number; include_children?: true; project_id?: number; work_type_id?: number } => {
    const num = (v: unknown): number | undefined => {
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : undefined;
    };
    const out: {
      unit_id?: number;
      include_children?: true;
      project_id?: number;
      work_type_id?: number;
    } = {};
    const unit = num(raw.unit_id);
    const project = num(raw.project_id);
    const workType = num(raw.work_type_id);
    if (unit !== undefined) out.unit_id = unit;
    if (raw.include_children === true || raw.include_children === "true")
      out.include_children = true;
    // Пара адресует работу целиком — половина пары бессмысленна, поэтому
    // принимаем её только вместе.
    if (project !== undefined && workType !== undefined) {
      out.project_id = project;
      out.work_type_id = workType;
    }
    return out;
  },
  component: WorkStatusRouteComponent,
});

function WorkStatusRouteComponent(): React.ReactElement {
  const search = workStatusRoute.useSearch();
  const navigate = workStatusRoute.useNavigate();
  const ledUnitIds = useSession((s) => s.user?.led_unit_ids);
  const effectiveUnitId =
    search.unit_id ?? (ledUnitIds?.length === 1 ? (ledUnitIds[0] ?? null) : null);
  const selected =
    search.project_id != null && search.work_type_id != null
      ? { project_id: search.project_id, work_type_id: search.work_type_id }
      : null;
  return (
    <Suspense fallback={<RouteFallback />}>
      <WorkStatusPage
        unitId={effectiveUnitId}
        includeChildren={search.include_children ?? false}
        selected={selected}
        onUnitChange={(unitId) =>
          // Смена подразделения обнуляет выбор: работа прошлого юнита в новой
          // сцене не существует и панель поймала бы 404.
          void navigate({ search: { unit_id: unitId }, replace: true })
        }
        onIncludeChildrenChange={(includeChildren) =>
          void navigate({
            search: {
              ...(effectiveUnitId != null ? { unit_id: effectiveUnitId } : {}),
              ...(includeChildren ? { include_children: true } : {}),
            },
            replace: true,
          })
        }
        onSelect={(pair) =>
          void navigate({
            search: {
              ...(effectiveUnitId != null ? { unit_id: effectiveUnitId } : {}),
              ...(search.include_children ? { include_children: true } : {}),
              ...(pair ?? {}),
            },
            replace: true,
          })
        }
      />
    </Suspense>
  );
}

const timeApprovalRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/time-approval",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.fetchQuery(meQueryOptions);
    const role = me?.role;
    const officeRole = role === "admin" || role === "office_staff";
    // worker/viewer вход не имеют (кроме acting — worker им не бывает).
    if (!officeRole && !actsOnUnit(me)) {
      throw redirect({ to: "/home" });
    }
    // Для не-консольного актора (plain pmo/office_staff) страница = self-редактор:
    // он доступен только участнику учёта (task 110 §2.1). Toggled-off без консоли
    // упёрся бы в 403 бэкенда (§1.2) → уводим на /home заранее. Консольных
    // (admin ∪ действует на юнит) флаг не касается.
    const hasConsole = role === "admin" || actsOnUnit(me);
    if (!hasConsole && !tracksTime(me)) {
      throw redirect({ to: "/home" });
    }
  },
  validateSearch: (
    raw: Record<string, unknown>,
  ): {
    tab?: TimeApprovalTab;
    week_start?: string;
    date?: string;
    unit_id?: number;
    include_children?: true;
    user_id?: number;
    entry_user?: number;
    entry_date?: string;
  } => {
    const out: {
      tab?: TimeApprovalTab;
      week_start?: string;
      date?: string;
      unit_id?: number;
      include_children?: true;
      user_id?: number;
      entry_user?: number;
      entry_date?: string;
    } = {};
    const isoRe = /^\d{4}-\d{2}-\d{2}$/;
    if (
      typeof raw.tab === "string" &&
      (TIME_APPROVAL_TABS as readonly string[]).includes(raw.tab)
    ) {
      out.tab = raw.tab as TimeApprovalTab;
    }
    if (typeof raw.week_start === "string" && isoRe.test(raw.week_start)) {
      out.week_start = raw.week_start;
    }
    if (typeof raw.date === "string" && isoRe.test(raw.date)) {
      out.date = raw.date;
    }
    const unitId = Number(raw.unit_id);
    if (Number.isInteger(unitId) && unitId > 0) {
      out.unit_id = unitId;
    }
    if (raw.include_children === true || raw.include_children === "true") {
      out.include_children = true;
    }
    // user_id — deep-link: открыть карточку дня сотрудника на mount (редирект
    // со старого backfill + «открыть в редакторе» из аудита).
    const userId = Number(raw.user_id);
    if (Number.isInteger(userId) && userId > 0) {
      out.user_id = userId;
    }
    // Состояние таба «Заполнить время» (задача 85 §5.3) — отдельные ключи, не
    // переиспускаем `date`/`unit_id`/`week_start` консоли (у тех своя семантика
    // «неделя подтверждения», дата зажата в окно недели; здесь дата свободная).
    const entryUser = Number(raw.entry_user);
    if (Number.isInteger(entryUser) && entryUser > 0) {
      out.entry_user = entryUser;
    }
    if (typeof raw.entry_date === "string" && isoRe.test(raw.entry_date)) {
      out.entry_date = raw.entry_date;
    }
    return out;
  },
  component: TimeApprovalRouteComponent,
});

function TimeApprovalRouteComponent(): React.ReactElement {
  const search = timeApprovalRoute.useSearch();
  const navigate = timeApprovalRoute.useNavigate();
  // Консоль подтверждения доступна admin и любому, кто действует на юнит (лид/зам
  // из орг-дерева, task 97/98); прочие впущенные роли (plain pmo/office_staff) —
  // только self-редактор без полосы табов (задача 99 §2.2).
  const isAdmin = useSession((s) => s.user?.role === "admin");
  const actsOnUnitFlag = useActsOnUnit();
  const hasConsole = isAdmin || actsOnUnitFlag;
  const today = format(new Date(), "yyyy-MM-dd");
  const weekStart = search.week_start ?? isoMonday(new Date());
  // Выбранный день держим внутри текущей недели: если date вне неё (или нет) —
  // дефолт на понедельник этой недели, либо сегодня, если оно в окне недели.
  const weekDays = Array.from({ length: 7 }, (_, i) =>
    format(addDays(parseISO(weekStart), i), "yyyy-MM-dd"),
  );
  const fallbackDate = weekDays.includes(today) ? today : weekStart;
  const selectedDate = search.date && weekDays.includes(search.date) ? search.date : fallbackDate;
  const selectedUnitId = search.unit_id ?? null;
  const includeChildren = search.include_children ?? false;
  const tab: TimeApprovalTab = search.tab ?? "console";
  // Состояние таба «Заполнить время» (задача 85 §5.3) — отдельные ключи; держим
  // в навигации консоли, чтобы выбор не терялся при переключении табов.
  const entryUserId = search.entry_user ?? null;
  const entryDate = search.entry_date ?? null;
  const entryKeys = {
    entry_user: entryUserId ?? undefined,
    entry_date: entryDate ?? undefined,
  };
  return (
    <TimeApprovalPage
      hasConsole={hasConsole}
      tab={tab}
      weekStart={weekStart}
      selectedDate={selectedDate}
      selectedUnitId={selectedUnitId}
      includeChildren={includeChildren}
      openUserId={search.user_id ?? null}
      entryUserId={entryUserId}
      entryDate={entryDate}
      onTabChange={(next) => {
        void navigate({
          search: {
            tab: next === "console" ? undefined : next,
            week_start: weekStart,
            date: selectedDate,
            unit_id: selectedUnitId ?? undefined,
            include_children: includeChildren || undefined,
            ...entryKeys,
          },
          replace: true,
        });
      }}
      onWeekStartChange={(next) => {
        void navigate({
          search: {
            tab: search.tab,
            week_start: next,
            unit_id: selectedUnitId ?? undefined,
            include_children: includeChildren || undefined,
            ...entryKeys,
          },
          replace: true,
        });
      }}
      onSelectedDateChange={(date) => {
        void navigate({
          search: {
            tab: search.tab,
            week_start: weekStart,
            date,
            unit_id: selectedUnitId ?? undefined,
            include_children: includeChildren || undefined,
            ...entryKeys,
          },
          replace: true,
        });
      }}
      onUnitChange={(unitId) => {
        void navigate({
          search: {
            tab: search.tab,
            week_start: weekStart,
            date: selectedDate,
            unit_id: unitId ?? undefined,
            include_children: includeChildren || undefined,
            ...entryKeys,
          },
          replace: true,
        });
      }}
      onIncludeChildrenChange={(next) => {
        void navigate({
          search: {
            tab: search.tab,
            week_start: weekStart,
            date: selectedDate,
            unit_id: selectedUnitId ?? undefined,
            include_children: next || undefined,
            ...entryKeys,
          },
          replace: true,
        });
      }}
      onEntryUserChange={(userId) => {
        void navigate({
          search: {
            tab: search.tab,
            week_start: weekStart,
            date: selectedDate,
            unit_id: selectedUnitId ?? undefined,
            include_children: includeChildren || undefined,
            entry_user: userId,
            entry_date: entryDate ?? undefined,
          },
          replace: true,
        });
      }}
      onEntryDateChange={(date) => {
        void navigate({
          search: {
            tab: search.tab,
            week_start: weekStart,
            date: selectedDate,
            unit_id: selectedUnitId ?? undefined,
            include_children: includeChildren || undefined,
            entry_user: entryUserId ?? undefined,
            entry_date: date,
          },
          replace: true,
        });
      }}
    />
  );
}

// ── /field/* — worker (PWA) layout ─────────────────────────────────────
// Separate layout from `_auth`'s AppShell: workers don't get sidebar/topbar
// chrome, only the mobile FieldShell rendered by the leaf pages themselves.
// Auth + capability check live here so a stale admin/PM bookmark to /field
// bounces back to /home rather than rendering an empty mobile screen.
function FieldLayout(): React.ReactElement {
  // Mirrors AuthLayout — hydrates the session store so FieldShell has the
  // user available immediately without an extra /me round-trip.
  useMe();
  return <Outlet />;
}

const fieldRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_field",
  component: FieldLayout,
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient.fetchQuery(meQueryOptions);
    if (!me) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
    if (me.must_change_password && location.pathname !== "/settings/change-password") {
      throw redirect({ to: "/settings/change-password" });
    }
    // PWA-слой пускает worker'а (его таймшит) и лида (лид-хаб §6.2, task 71 §6.1;
    // авторитет из орг-дерева, task 97) всегда. Прочих офисных (pmo/admin/viewer)
    // пускаем ТОЛЬКО с мобильного устройства: десктоп-приложение под мобайл не
    // адаптировано, поэтому телефон = «только ввод времени» (task 73 §2.1). На
    // десктопе у офисного нет /field-UI → возвращаем на /home.
    if (me.role !== "worker" && !actsOnUnit(me) && !isMobileDevice()) {
      throw redirect({ to: "/home" });
    }
  },
});

export const fieldIndexRoute = createRoute({
  getParentRoute: () => fieldRoute,
  path: "/field",
  beforeLoad: async ({ context }) => {
    // `/field` — это worker-таймшит (WorkerOnly-эндпоинты). Лид сюда не садится
    // напрямую — его точка входа лид-хаб; «Моё время» хаб разводит сам (§6.2).
    // Иначе руководитель упёрся бы в 403 на /field/timesheet/* (task 71, 97 §2.1).
    const me = await context.queryClient.fetchQuery(meQueryOptions);
    if (actsOnUnit(me)) {
      throw redirect({ to: "/field/lead" });
    }
  },
  validateSearch: (raw: Record<string, unknown>): { date?: string } => {
    const out: { date?: string } = {};
    const rawDate = raw.date;
    if (typeof rawDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
      out.date = rawDate;
    }
    return out;
  },
  component: FieldTimesheetPage,
});

const fieldSignRoute = createRoute({
  getParentRoute: () => fieldRoute,
  path: "/field/sign",
  component: FieldSignPage,
});

// Мобильный хаб руководителя (task 71 §6.1 — точка входа лида на телефоне;
// §6.2 разворачивает в полный M6). Только лид/admin: worker'а отсюда
// возвращаем в его таймшит. Под `fieldRoute` (PWA-layout без LeftRail).
const leadHubRoute = createRoute({
  getParentRoute: () => fieldRoute,
  path: "/field/lead",
  beforeLoad: async ({ context }) => {
    // Лид-хаб — только для лида/админа. worker и офисные (pmo/viewer, теперь
    // допущенные на /field с телефона — 73 §2.1) сюда не садятся: их точка
    // входа — свой таймшит на /field. Лид выводится из орг-дерева (task 97 §2.1).
    const me = await context.queryClient.fetchQuery(meQueryOptions);
    if (!actsOnUnit(me) && me?.role !== "admin") {
      throw redirect({ to: "/field" });
    }
  },
  component: LeadHubPage,
});

// Мобильный экран подтверждения (M6, §6.2) — ветка «Подтверждение» хаба.
// search: `week_start` / `date` (как у десктоп-`/time-approval`) + `mytime`
// (1 = ветка «Моё время» хаба: открыть self-entry мастера на mount).
const leadApprovalRoute = createRoute({
  getParentRoute: () => fieldRoute,
  path: "/field/lead/approve",
  beforeLoad: async ({ context }) => {
    // Только лид/админ (M6-подтверждение). worker и офисные (73 §2.1) → /field.
    // Лид выводится из орг-дерева, а не из роли (task 97 §2.1).
    const me = await context.queryClient.fetchQuery(meQueryOptions);
    if (!actsOnUnit(me) && me?.role !== "admin") {
      throw redirect({ to: "/field" });
    }
  },
  validateSearch: (
    raw: Record<string, unknown>,
  ): { week_start?: string; date?: string; unit_id?: number; mytime?: boolean } => {
    const out: { week_start?: string; date?: string; unit_id?: number; mytime?: boolean } = {};
    const isoRe = /^\d{4}-\d{2}-\d{2}$/;
    if (typeof raw.week_start === "string" && isoRe.test(raw.week_start)) {
      out.week_start = raw.week_start;
    }
    if (typeof raw.date === "string" && isoRe.test(raw.date)) {
      out.date = raw.date;
    }
    const unitId = Number(raw.unit_id);
    if (Number.isInteger(unitId) && unitId > 0) {
      out.unit_id = unitId;
    }
    if (raw.mytime === "1" || raw.mytime === "true" || raw.mytime === true) {
      out.mytime = true;
    }
    return out;
  },
  component: LeadApprovalRouteComponent,
});

function LeadApprovalRouteComponent(): React.ReactElement {
  const search = leadApprovalRoute.useSearch();
  const navigate = leadApprovalRoute.useNavigate();
  const today = format(new Date(), "yyyy-MM-dd");
  const weekStart = search.week_start ?? isoMonday(new Date());
  const weekDays = Array.from({ length: 7 }, (_, i) =>
    format(addDays(parseISO(weekStart), i), "yyyy-MM-dd"),
  );
  const fallbackDate = weekDays.includes(today) ? today : weekStart;
  const selectedDate = search.date && weekDays.includes(search.date) ? search.date : fallbackDate;
  const selectedUnitId = search.unit_id ?? null;
  return (
    <LeadApprovalPage
      weekStart={weekStart}
      selectedDate={selectedDate}
      selectedUnitId={selectedUnitId}
      openMyTime={search.mytime === true}
      onWeekStartChange={(next) => {
        void navigate({
          search: { week_start: next, unit_id: selectedUnitId ?? undefined },
          replace: true,
        });
      }}
      onSelectedDateChange={(date) => {
        void navigate({
          search: { week_start: weekStart, date, unit_id: selectedUnitId ?? undefined },
          replace: true,
        });
      }}
      onUnitChange={(unitId) => {
        void navigate({
          search: { week_start: weekStart, date: selectedDate, unit_id: unitId ?? undefined },
          replace: true,
        });
      }}
    />
  );
}

// Выделенная field-поверхность «Статусы работ» (задача 122 §1.1) — ветка хаба
// `/field/lead`. Гейт — паритет с хабом: admin (по роли) ∪ «действует на юнит».
// Под `fieldRoute` (PWA-layout без LeftRail).
const fieldWorkStatusRoute = createRoute({
  getParentRoute: () => fieldRoute,
  path: "/field/lead/work-status",
  beforeLoad: async ({ context }) => {
    // Тот же предикат, что у лид-хаба/подтверждения: лид выводится из орг-дерева
    // (task 97 §2.1), admin — по роли (его acting-список пуст). worker и прочие
    // офисные сюда не садятся → возвращаем в свой таймшит на /field.
    const me = await context.queryClient.fetchQuery(meQueryOptions);
    if (!actsOnUnit(me) && me?.role !== "admin") {
      throw redirect({ to: "/field" });
    }
  },
  component: FieldWorkStatusPage,
});

const approvalsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/approvals",
  component: ApprovalsPage,
});

const approvalDetailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/approvals/$simulationId",
  parseParams: (raw: Record<string, string>) => ({ simulationId: Number(raw.simulationId) }),
  stringifyParams: (params: { simulationId: number }) => ({
    simulationId: String(params.simulationId),
  }),
  component: ApprovalDetailRouteComponent,
});

function ApprovalDetailRouteComponent(): React.ReactElement {
  const { simulationId } = approvalDetailRoute.useParams();
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <ApprovalDetailPage simulationId={simulationId} />
      </Suspense>
    </ChunkErrorBoundary>
  );
}

const routeTree = rootRoute.addChildren([
  anonRoute.addChildren([loginRoute]),
  authRoute.addChildren([
    indexRoute,
    homeRoute,
    settingsRoute,
    changePasswordRoute,
    ganttPocRoute,
    taskNodePocRoute,
    projectsRoute,
    newProjectRoute,
    projectDetailRoute,
    portfolioRoute,
    loadRoute,
    peopleRoute,
    newPersonRoute,
    unitDetailRoute,
    peopleDetailRoute,
    risksRoute,
    auditRoute,
    calendarRoute,
    templatesRoute,
    templateEditorRoute,
    adminCatalogsRoute,
    adminBackfillRoute,
    approvalsRoute,
    approvalDetailRoute,
    timeApprovalRoute,
    workStatusRoute,
  ]),
  fieldRoute.addChildren([
    fieldIndexRoute,
    fieldSignRoute,
    leadHubRoute,
    leadApprovalRoute,
    fieldWorkStatusRoute,
  ]),
]);

export interface CreateAppRouterOptions {
  history?: RouterHistory;
}

export function createAppRouter(queryClient: QueryClient, options: CreateAppRouterOptions = {}) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    // Suspense fallback for code-split routes (task 106 §1.2): wraps every
    // route match in a boundary that shows the brand-mark skeleton while the
    // lazy chunk resolves. `defaultPreload: "intent"` warms the chunk on hover,
    // so this rarely flashes on desktop.
    defaultPendingComponent: RouteFallback,
    rewrite: {
      input: ({ url }) => {
        for (const key of ["unit_ids", "position_ids"]) {
          const values = url.searchParams.getAll(key);
          if (values.length > 1) url.searchParams.set(key, JSON.stringify(values));
        }
        return url;
      },
      output: ({ url }) => {
        for (const key of ["unit_ids", "position_ids"]) {
          const value = url.searchParams.get(key);
          if (value == null) continue;
          try {
            const ids = JSON.parse(value);
            if (!Array.isArray(ids)) continue;
            url.searchParams.delete(key);
            ids.forEach((id) => url.searchParams.append(key, String(id)));
          } catch {
            // A scalar route-search value belongs to the router's normal serializer.
          }
        }
        return url;
      },
    },
    ...options,
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module "@tanstack/react-router" {
  interface Register {
    router: AppRouter;
  }
}

export function AppRouter({ router }: { router: AppRouter }): React.ReactElement {
  return <RouterProvider router={router} />;
}
